import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import S from "./Accordion.styled";

import { ColorOptions, IWPImage, IWPTitleParts, IWPVideo } from "@/src/types";
import { Icon, Image, Wysiwyg } from "@/styles/components";

import Youtube from "@/components/global-components/Youtube";
import TitleParts from "@/components/global-components/TitleParts";
import { isLight } from "@/src/helpers";
import { motionAnimation } from "@/styles/global";

export interface AccordionProps {
  blockId: string;
  titleParts: IWPTitleParts;
  titlePartsLarge: IWPTitleParts;
  accordionPlacement: "left" | "right";
  textOrImage: string;
  video: IWPVideo;
  colorPlacement: "items" | "background";
  color: ColorOptions;
  textAlign: "center" | "top";
  title: string;
  titleLarge: string;
  text: string;
  image: {
    image: IWPImage;
    imageText?: string;
    textBackground: "fill" | "gradient";
  };
  items: {
    title: string;
    content: {
      subTitle: string;
      text: string;
      includeImage?: "true" | "false";
      mediaType?: "image" | "video";
      video: IWPVideo;
      image: {
        image: IWPImage;
        imageText?: string;
        textBackground: "fill" | "gradient";
      };
    }[];
  }[];
}

const Accordion: React.FC<AccordionProps> = ({
  blockId,
  titleParts,
  titlePartsLarge,
  accordionPlacement,
  textOrImage,
  video,
  colorPlacement,
  color,
  textAlign,
  title,
  titleLarge,
  text,
  image,
  items,
}) => {
  const coloredBackground = colorPlacement === "background";
  const headingColor =
    colorPlacement !== "background"
      ? "black"
      : color === "yellow"
        ? "brown"
        : isLight(color)
          ? "black"
          : "creme";
  const textColor =
    color === "yellow" ? "brown" : isLight(color) ? "black" : "creme";
  const [open, setOpen] = useState<number | undefined>(undefined);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile when resizing
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const titlePartsTextColor = () => {
    if (colorPlacement === "items") {
      return "white";
    } else if (color === "black" || color === "brown") {
      return color;
    } else {
      return color;
    }
  };

  const renderItems = items?.map(({ title, content }, index) => {
    const key = index + title;
    const isOpen = open === index;

    const renderContent = content.map(
      ({ subTitle, includeImage, mediaType, video, text, image }, index) => {
        const key = index + subTitle;
        return (
          <S.ContentWrapper key={key}>
            <S.TitleSmall $color={textColor}>{subTitle}</S.TitleSmall>
            <Wysiwyg color={textColor} html={text} />
            {includeImage === "true" && mediaType === "image" ? (
              <>
                <Image
                  image={image.image}
                  imageText={image.imageText}
                  textBackground={image.textBackground}
                  margin="24px 0"
                  radius={6}
                />
              </>
            ) : null}

            {includeImage === "true" && mediaType === "video" && video && (
              <Youtube video={video} />
            )}
          </S.ContentWrapper>
        );
      }
    );

    return (
      <S.Row
        key={key}
        $color={color}
        $isOpen={isOpen}
        $background={coloredBackground}
        $colorPlacement={colorPlacement}
      >
        <S.AccordionTitleWrapper
          onClick={() => {
            setOpen(isOpen ? undefined : index);
          }}
        >
          <S.AccordionTitle $color={textColor}>{title}</S.AccordionTitle>
          <S.IconWrapper>
            <motion.div
              animate={{
                rotate: isOpen ? 180 : 0,
                translateY: isOpen ? -2 : 2,
              }}
            >
              <Icon
                src="ChevronDown"
                width={24}
                height={24}
                color={textColor}
              />
            </motion.div>
          </S.IconWrapper>
        </S.AccordionTitleWrapper>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <S.SubRow key={index}>{renderContent}</S.SubRow>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </S.Row>
    );
  });

  return (
    <S.Section
      $textColor={textColor}
      as={motion.section}
      {...motionAnimation}
      id={blockId}
    >
      <S.Backdrop $background={coloredBackground ? color : undefined}>
        <S.Inner>
          <S.Wrapper $placement={accordionPlacement} $textAlignment={textAlign}>
            <S.TextWrapper>
              {textOrImage === "image" ? (
                <>
                  {title && <S.Title $color={headingColor}>{title}</S.Title>}
                  {titleParts && (
                    <S.Title>
                      <TitleParts
                        titleParts={titleParts}
                        backgroundColor={titlePartsTextColor()}
                      />
                    </S.Title>
                  )}
                  <Image
                    image={image.image}
                    height={isMobile ? 214 : 550}
                    width={"100%"}
                    radius={20}
                    imageText={image.imageText}
                    textBackground={image.textBackground}
                  />
                </>
              ) : textOrImage === "video" && video ? (
                <>
                  {title && <S.Title color={headingColor}>{title}</S.Title>}
                  {titleParts && (
                    <S.Title>
                      <TitleParts
                        titleParts={titleParts}
                        backgroundColor={titlePartsTextColor()}
                      />
                    </S.Title>
                  )}
                  <Youtube video={video} />
                </>
              ) : (
                <>
                  {title && (
                    <S.TitleMedium $color={headingColor}>{title}</S.TitleMedium>
                  )}
                  {titleParts && (
                    <S.Title>
                      <TitleParts
                        backgroundColor={titlePartsTextColor()}
                        titleParts={titleParts}
                      />
                    </S.Title>
                  )}
                  {titleLarge && (
                    <S.TitleLarge $color={headingColor}>
                      {titleLarge}
                    </S.TitleLarge>
                  )}
                  {titlePartsLarge && (
                    <S.TitleLarge>
                      <TitleParts
                        backgroundColor={titlePartsTextColor()}
                        titleParts={titlePartsLarge}
                      />
                    </S.TitleLarge>
                  )}
                  <Wysiwyg color={headingColor} html={text} />
                </>
              )}
            </S.TextWrapper>
            <S.Rows>{renderItems}</S.Rows>
          </S.Wrapper>
        </S.Inner>
      </S.Backdrop>
    </S.Section>
  );
};

export default Accordion;
